import { useNavigate, useParams } from 'react-router-dom'
import { useChatStore } from '../../store/chatStore'
import { useAuthStore } from '../../store/authStore'
import { formatDistanceToNow } from 'date-fns'

const ChatList = ({ chats }) => {
  const navigate = useNavigate()
  const { chatId } = useParams()
  const { setCurrentChat } = useChatStore()
  const { user } = useAuthStore()

  const handleChatClick = (chat) => {
    setCurrentChat(chat)
    navigate(`/chat/${chat._id}`)
  }

  const getChatDisplayName = (chat) => {
    if (chat.isGroupChat) {
      return chat.name || 'Group Chat'
    }
    
    // For direct chats, show the other participant's name
    const otherParticipant = chat.participants.find(p => p._id !== user?._id)
    return otherParticipant?.username || 'Unknown User'
  }

  const getChatAvatar = (chat) => {
    if (chat.isGroupChat) {
      return chat.avatar || null
    }
    
    const otherParticipant = chat.participants.find(p => p._id !== user?._id)
    return otherParticipant?.avatar || null
  }

  const getLastMessagePreview = (chat) => {
    if (!chat.lastMessage) return 'No messages yet'
    
    const message = chat.lastMessage
    if (message.isDeleted) return 'Message deleted'
    if (message.messageType === 'image') return '📷 Image'
    if (message.messageType === 'file') return '📎 File'
    
    return message.content || 'Message'
  }

  const isOnline = (chat) => {
    if (chat.isGroupChat) return false
    
    const otherParticipant = chat.participants.find(p => p._id !== user?._id)
    return otherParticipant?.isOnline || false
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {chats.map((chat) => {
        const isActive = chatId === chat._id
        const displayName = getChatDisplayName(chat)
        const avatar = getChatAvatar(chat)
        const lastMessagePreview = getLastMessagePreview(chat)
        const userIsOnline = isOnline(chat)
        const unreadCount = chat.unreadCount || 0

        return (
          <div
            key={chat._id}
            onClick={() => handleChatClick(chat)}
            className={`
              chat-item
              ${isActive ? 'chat-item-active' : ''}
            `}
          >
            {/* Avatar */}
            <div className="flex-shrink-0 relative">
              {avatar ? (
                <img
                  src={avatar}
                  alt={displayName}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  {chat.isGroupChat ? (
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.12M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.12M7 20v-2c0-.656.126-1.283.356-1.857M9 9a3 3 0 116 0 3 3 0 01-6 0z" />
                    </svg>
                  ) : (
                    <span className="text-primary-600 dark:text-primary-400 font-medium text-lg">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              )}
              
              {/* Online indicator for direct chats */}
              {!chat.isGroupChat && userIsOnline && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
              )}
            </div>

            {/* Chat Info */}
            <div className="flex-1 min-w-0 ml-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {displayName}
                </h3>
                
                {chat.lastMessage && (
                  <time className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                    {formatDistanceToNow(new Date(chat.lastMessage.createdAt), { addSuffix: true })}
                  </time>
                )}
              </div>
              
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate pr-2">
                  {lastMessagePreview}
                </p>
                
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-primary-600 rounded-full min-w-[1.25rem]">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              
              {/* Participants preview for group chats */}
              {chat.isGroupChat && chat.participants.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-500 truncate mt-1">
                  {chat.participants.slice(0, 3).map(p => p.username).join(', ')}
                  {chat.participants.length > 3 && ` +${chat.participants.length - 3} more`}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ChatList